import { useRef, useState } from 'react';

import { CloseIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Image,
  VStack,
  HStack,
  Box,
  IconButton,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from 'utils/hooks/screenBreakpoints';

type PreviewFile = {
  file: File;
  previewUrl?: string;
};

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (formData: FormData) => Promise<void>;
  isLoading: boolean;
}

export function PaymentUploadModal({
  isOpen,
  onClose,
  onUpload,
  isLoading,
}: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<PreviewFile[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    const newFiles = selectedFiles.filter(
      (newFile) =>
        !files.some(
          ({ file }) =>
            file.name === newFile.name &&
            file.size === newFile.size &&
            file.lastModified === newFile.lastModified,
        ),
    );
    const newPreviewFiles = newFiles.map((file) => ({
      file,
      previewUrl:
        file.type.startsWith('image/') || file.type.startsWith('video/')
          ? URL.createObjectURL(file)
          : undefined,
    }));

    setFiles([...files, ...newPreviewFiles]);
  };

  const handleRemoveFile = (idx: number) => {
    const newFiles = files.filter((_, i) => i !== idx);
    setFiles(newFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    const formData = new FormData();
    files.forEach(({ file }) => {
      formData.append('file', file, file.name);
    });
    await onUpload(formData);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };
  const isMobile = useIsMobile();
  const { t } = useTranslation('payments');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={isMobile ? 'full' : 'lg'}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader padding='16px'>
          {t('payment_upload_modal_header')}
        </ModalHeader>
        <ModalCloseButton top='16px' right='16px' />
        <Text
          mt='16px'
          ml='16px'
          fontSize='sm'
          fontWeight='400'
          color='grey.tertiaryText'
        >
          {t('payment_upload_modal_description')}
        </Text>
        <ModalBody padding='0px 16px'>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*,application/pdf,video/*'
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {files.length > 0 && (
            <VStack mt={4} align='start' spacing={2} w='100%'>
              {files.map(({ file, previewUrl }, idx) => (
                <HStack key={file.name + idx} spacing={4} w='100%'>
                  {file.type.startsWith('image/') && previewUrl ? (
                    <Image
                      src={previewUrl}
                      boxSize='200px'
                      objectFit='cover'
                      alt={file.name}
                      borderRadius='md'
                      border='1px solid #eee'
                    />
                  ) : file.type.startsWith('video/') && previewUrl ? (
                    <Box
                      boxSize='200px'
                      borderRadius='md'
                      border='1px solid #eee'
                      overflow='hidden'
                    >
                      <video
                        src={previewUrl}
                        width='200px'
                        height='200px'
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ) : file.type === 'application/pdf' ? (
                    <Box
                      minWidth='200px'
                      height='200px'
                      bg='gray.100'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='md'
                      border='1px solid #eee'
                    >
                      <Text fontSize='xs'>PDF</Text>
                    </Box>
                  ) : (
                    <Box
                      boxSize='200px'
                      bg='gray.100'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='md'
                      border='1px solid #eee'
                    >
                      <Text fontSize='xs'>
                        {file.name.split('.').pop()?.toUpperCase()}
                      </Text>
                    </Box>
                  )}
                  <Text fontSize='sm' noOfLines={1} maxW='160px'>
                    {file.name}
                  </Text>
                  <IconButton
                    aria-label={t('payment_upload_modal_remove_button')}
                    icon={<CloseIcon />}
                    size='sm'
                    variant='ghost'
                    onClick={() => handleRemoveFile(idx)}
                  />
                </HStack>
              ))}
            </VStack>
          )}
        </ModalBody>
        <Box
          position='sticky'
          bottom={0}
          left={0}
          right={0}
          bg='white'
          p={4}
          zIndex={1}
        >
          <Button
            variant='success'
            w='100%'
            onClick={() => fileInputRef.current?.click()}
            isLoading={isLoading}
          >
            {t('payment_upload_modal_choose_button')}
          </Button>
        </Box>
        <ModalFooter>
          <Button
            onClick={handleUpload}
            isLoading={isLoading}
            isDisabled={files.length === 0}
          >
            {t('payment_upload_modal_send_button')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
